import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import displayINRCurrency from "../helpers/displayCurrency";
import SummaryApi from "../common";
import axios from "axios";
import { authHeader } from "../helpers/auth-header";
import { walletUpdate } from "../store/cartSlice";
import { useDispatch } from "react-redux";
import WalletInstructions from "../components/WalletInstructions";
import { HiWallet, HiArrowLeft, HiArrowRight, HiClock, HiCheckCircle, HiBell } from "react-icons/hi2";
import { MdTrendingUp, MdTrendingDown, MdHistory } from "react-icons/md";
import { useNotification } from "../context/NotificationContext";
import { ToastCommon } from "../common/Toast";
import LoadingButton from "../components/LoadingButton";
import QrScannerModal from "../components/QrScannerModal";

const WalletOverviewCompact = () => {
    const dispatch = useDispatch();
    const { unreadCount } = useNotification();
    const [walletData, setWalletData] = useState({
        availableAmount: 0,
        pendingAmount: 0,
        totalCashbackEarned: 0,
        transactions: [],
    });

    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const transactionsPerPage = 10;

    const [giftOpen, setGiftOpen] = useState(false);
    const [giftStep, setGiftStep] = useState("form"); // form | confirm | success
    const [giftSubmitting, setGiftSubmitting] = useState(false);
    const [giftForm, setGiftForm] = useState({
        recipientPublicId: "",
        amount: "",
        message: "",
    });
    const [giftErrors, setGiftErrors] = useState({});
    const [giftResult, setGiftResult] = useState(null);
    const [qrOpen, setQrOpen] = useState(false);
    const [giftRecipient, setGiftRecipient] = useState(null); // { publicId, name }

    const fetchWallet = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(SummaryApi.wallet.url, {
                headers: authHeader(),
            });
            if (data?.walletInfo) {
                setWalletData(data.walletInfo);
                dispatch(walletUpdate(data?.walletInfo?.availableAmount))
            } else {
                console.error("No wallet data found in the response.");
            }
        } catch (err) {
            console.error("Error fetching wallet data:", err.message || err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWallet();
    }, []);

    const resetGift = () => {
        setGiftStep("form");
        setGiftSubmitting(false);
        setGiftForm({ recipientPublicId: "", amount: "", message: "" });
        setGiftErrors({});
        setGiftResult(null);
        setGiftRecipient(null);
    };

    const openScanFirst = () => {
        resetGift();
        setGiftOpen(false);
        setQrOpen(true);
    };

    const closeGift = () => {
        setQrOpen(false);
        setGiftOpen(false);
    };

    const onQrDetected = (payload) => {
        const pid = payload?.publicId || "";
        const name = payload?.name || null;
        setGiftRecipient(pid ? { publicId: pid, name } : null);
        setGiftForm((p) => ({ ...p, recipientPublicId: pid }));
        setGiftErrors((p) => ({ ...p, recipientPublicId: null }));
        setGiftOpen(true);
        setGiftStep("form");
        ToastCommon.success(name ? `Recipient: ${name}` : "Recipient filled from QR.");
    };

    const validateGift = (form) => {
        const nextErrors = {};
        const recipientPublicId = String(form?.recipientPublicId || "").trim();
        const amountRaw = String(form?.amount || "").trim();
        const message = String(form?.message || "");

        if (!/^[a-z0-9]{12}$/i.test(recipientPublicId)) {
            nextErrors.recipientPublicId = "Recipient publicId must be 12 character alphanumeric.";
        }

        const amountInt = Number(amountRaw);
        if (!Number.isInteger(amountInt) || amountInt <= 0) {
            nextErrors.amount = "Amount must be a positive integer (₹).";
        }

        if (message.length > 200) {
            nextErrors.message = "Message must be at most 200 characters.";
        }

        return nextErrors;
    };

    const goToConfirm = () => {
        const errs = validateGift(giftForm);
        setGiftErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setGiftStep("confirm");
    };

    const submitGift = async () => {
        const errs = validateGift(giftForm);
        setGiftErrors(errs);
        if (Object.keys(errs).length > 0) return;

        const recipientPublicId = String(giftForm.recipientPublicId || "").trim();
        const amount = Number(String(giftForm.amount || "").trim());
        const message = String(giftForm.message || "").trim();

        setGiftSubmitting(true);
        try {
            const payload = {
                recipientPublicId,
                amount,
                ...(message ? { message } : {}),
            };
            const { data } = await axios.post(SummaryApi.walletGift.url, payload, {
                headers: authHeader(),
            });
            setGiftResult(data?.transfer || null);
            setGiftStep("success");
            ToastCommon.success(data?.message || "Gift sent successfully.");
            await fetchWallet();
        } catch (err) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.message || "Failed to send gift.";
            if (status === 404) ToastCommon.error("Recipient not found.");
            else if (status === 409) ToastCommon.warning(msg);
            else if (status === 400) ToastCommon.warning(msg);
            else if (status === 422) ToastCommon.warning(msg);
            else ToastCommon.error(msg);
        } finally {
            setGiftSubmitting(false);
        }
    };

    // Pagination Logic
    const indexOfLastTransaction = currentPage * transactionsPerPage;
    const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
    const currentTransactions = walletData.transactions.slice(indexOfFirstTransaction, indexOfLastTransaction);

    const totalPages = Math.ceil(walletData.transactions.length / transactionsPerPage);

    const recipientDisplayName =
        (giftRecipient?.name && String(giftRecipient.name).trim()) ||
        (giftForm.recipientPublicId?.trim() ? "User" : "");
    const hasScannedRecipient = !!String(giftForm.recipientPublicId || "").trim();

    return (
        <section className="bg-gradient-to-br from-gray-50 to-orange-50 min-h-screen py-5 md:py-6">
            <div className="container mx-auto px-3 md:px-4 max-w-6xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-5 md:mb-6">
                    <div className="text-center flex-1">
                        <div className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-full mb-3">
                            <HiWallet className="text-2xl md:text-3xl text-orange-500" />
                        </div>
                        <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent mb-1">
                            My Wallet
                        </h1>
                        <p className="text-gray-600 text-sm md:text-base">
                            Manage your cashback and transactions
                        </p>
                    </div>
                    {/* Notification Link */}
                    <Link
                        to="/notifications"
                        className="relative p-2 rounded-lg bg-gradient-to-br from-orange-50 to-yellow-50 hover:from-orange-100 hover:to-yellow-100 transition-all duration-200 hover:scale-105 transform border border-orange-200 ml-3"
                        title="Notifications"
                    >
                        <HiBell className="text-xl md:text-2xl text-orange-500" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white min-w-[18px] h-4.5 px-1 rounded-full flex items-center justify-center text-[10px] font-bold shadow-lg border-2 border-white">
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </Link>
                </div>

                {/* Wallet Summary Cards */}
                <div className="grid grid-cols-3 gap-2 md:gap-4 mb-5 md:mb-6">
                    {/* Available Balance */}
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl md:rounded-2xl shadow-lg p-3 md:p-5 text-white transform hover:scale-[1.02] transition-transform duration-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                                <HiCheckCircle className="text-base md:text-xl" />
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] md:text-sm text-green-100 font-medium mb-0.5 leading-tight">Available Balance</p>
                                <p className="text-xl md:text-[28px] font-bold leading-tight">
                                    {displayINRCurrency(walletData.availableAmount)}
                                </p>
                            </div>
                        </div>
                        <p className="text-[10px] md:text-sm text-green-100 leading-tight">Ready to use</p>
                    </div>

                    {/* Pending Amount */}
                    <div className="bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl md:rounded-2xl shadow-lg p-3 md:p-5 text-white transform hover:scale-[1.02] transition-transform duration-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                                <HiClock className="text-base md:text-xl" />
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] md:text-sm text-yellow-100 font-medium mb-0.5 leading-tight">Pending</p>
                                <p className="text-xl md:text-[28px] font-bold leading-tight">
                                    {displayINRCurrency(walletData.pendingAmount)}
                                </p>
                            </div>
                        </div>
                        <p className="text-[10px] md:text-sm text-yellow-100 leading-tight">Under verification</p>
                    </div>

                    {/* Total Cashback */}
                    <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl md:rounded-2xl shadow-lg p-3 md:p-5 text-white transform hover:scale-[1.02] transition-transform duration-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                                <MdTrendingUp className="text-base md:text-xl" />
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] md:text-sm text-blue-100 font-medium mb-0.5 leading-tight">Total Cashback</p>
                                <p className="text-xl md:text-[28px] font-bold leading-tight">
                                    {displayINRCurrency(walletData.totalCashbackEarned)}
                                </p>
                            </div>
                        </div>
                        <p className="text-[10px] md:text-sm text-blue-100 leading-tight">All time earnings</p>
                    </div>
                </div>

                {/* Send Gift */}
                <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 border border-gray-100 mb-5 md:mb-6">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-gray-900">Send Gift</h2>
                            <p className="text-xs md:text-sm text-gray-600 mt-1">
                                Scan their QR, then enter amount — like UPI.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={openScanFirst}
                            className="px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                        >
                            Send
                        </button>
                    </div>
                </div>

                <WalletInstructions />

                {/* Transaction History */}
                <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 border border-gray-100">
                    <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-200">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center">
                            <MdHistory className="text-xl text-purple-600" />
                        </div>
                        <h2 className="text-lg md:text-xl font-bold text-gray-900">Transaction History</h2>
                    </div>

                    {loading ? (
                        <div className="flex flex-col justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mb-4"></div>
                            <p className="text-gray-500">Loading transactions...</p>
                        </div>
                    ) : walletData.transactions.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                                <MdHistory className="text-4xl text-gray-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Transactions Yet</h3>
                            <p className="text-gray-500">Your transaction history will appear here</p>
                        </div>
                    ) : (
                        <>
                            <ul className="space-y-2.5">
                                {currentTransactions.map((transaction, index) => {
                                    const isPending = transaction.status === "pending";
                                    const isCredit = transaction.type === "credit";
                                
                                    return (
                                        <li
                                            key={index}
                                            className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 hover:shadow-sm ${
                                                isPending
                                                    ? "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200"
                                                    : "bg-gray-50 border-gray-200 hover:border-gray-300"
                                            }`}
                                        >
                                            {/* Icon */}
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                isCredit 
                                                    ? "bg-green-100" 
                                                    : "bg-red-100"
                                            }`}>
                                                {isCredit ? (
                                                    <MdTrendingUp className={`text-xl ${isCredit ? "text-green-600" : "text-red-600"}`} />
                                                ) : (
                                                    <MdTrendingDown className="text-xl text-red-600" />
                                                )}
                                            </div>

                                            {/* Transaction Details */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                                                            isCredit
                                                                ? "bg-green-100 text-green-700"
                                                                : "bg-red-100 text-red-700"
                                                        }`}
                                                    >
                                                        {transaction.type === "credit" ? "Credit" : "Debit"}
                                                    </span>
                                                    {isPending && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-yellow-100 text-yellow-700">
                                                            <HiClock className="mr-1" size={12} />
                                                            Pending
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm font-medium text-gray-900 truncate leading-snug">
                                                    {transaction.description}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {new Date(transaction.createdAt).toLocaleDateString("en-US", {
                                                        day: "numeric",
                                                        month: "short",
                                                        year: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit"
                                                    })}
                                                </p>
                                            </div>

                                            {/* Amount */}
                                            <div className="text-right flex-shrink-0">
                                                <p
                                                    className={`text-lg md:text-xl font-bold ${
                                                        isCredit ? "text-green-600" : "text-red-600"
                                                    }`}
                                                >
                                                    {isCredit ? "+" : "-"}
                                                    {displayINRCurrency(transaction.amount)}
                                                </p>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                                    <button
                                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                    >
                                        <HiArrowLeft className="text-base" />
                                        Previous
                                    </button>
                                    <span className="text-gray-700 text-sm font-medium">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                    >
                                        Next
                                        <HiArrowRight className="text-base" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Gift Modal */}
                {giftOpen && (
                    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center">
                        <div
                            className="absolute inset-0 bg-black/50"
                            onClick={closeGift}
                            aria-hidden="true"
                        />
                        <div className="relative bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-100 p-5 sm:p-6 m-0 sm:m-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-bold text-gray-900">
                                    {giftStep === "success"
                                        ? "Gift sent"
                                        : giftStep === "confirm"
                                          ? "Confirm"
                                          : "Send money"}
                                </h3>
                                <button
                                    type="button"
                                    onClick={closeGift}
                                    className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center"
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                            </div>

                            {giftStep === "form" && (
                                <div className="space-y-5">
                                    {hasScannedRecipient ? (
                                        <>
                                            <div className="text-center pt-2 pb-1">
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                                                    Sending to
                                                </p>
                                                <p className="text-2xl font-bold text-gray-900 tracking-tight">
                                                    {recipientDisplayName}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="sr-only">Amount in rupees</label>
                                                <div className="flex items-baseline justify-center gap-1 border-b-2 border-gray-200 focus-within:border-orange-400 pb-2 transition-colors">
                                                    <span className="text-2xl font-semibold text-gray-400">₹</span>
                                                    <input
                                                        value={giftForm.amount}
                                                        onChange={(e) => {
                                                            setGiftForm((p) => ({ ...p, amount: e.target.value }));
                                                            setGiftErrors((p) => ({ ...p, amount: null }));
                                                        }}
                                                        placeholder="0"
                                                        inputMode="numeric"
                                                        autoFocus
                                                        className={`w-full max-w-[200px] bg-transparent text-center text-4xl font-bold text-gray-900 placeholder:text-gray-300 focus:outline-none ${
                                                            giftErrors.amount ? "text-red-600" : ""
                                                        }`}
                                                    />
                                                </div>
                                                {giftErrors.amount && (
                                                    <p className="text-xs text-red-600 mt-2 text-center">{giftErrors.amount}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="text-xs font-medium text-gray-500">Add a note (optional)</label>
                                                <input
                                                    type="text"
                                                    value={giftForm.message}
                                                    onChange={(e) => {
                                                        setGiftForm((p) => ({
                                                            ...p,
                                                            message: e.target.value.slice(0, 200),
                                                        }));
                                                        setGiftErrors((p) => ({ ...p, message: null }));
                                                    }}
                                                    maxLength={200}
                                                    placeholder="What's this for?"
                                                    className={`mt-1.5 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-orange-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-100 ${
                                                        giftErrors.message ? "border-red-300" : ""
                                                    }`}
                                                />
                                                {giftErrors.message && (
                                                    <p className="text-xs text-red-600 mt-1">{giftErrors.message}</p>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-2 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={goToConfirm}
                                                    className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-bold text-base shadow-lg active:scale-[0.99] transition-transform"
                                                >
                                                    Continue
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={closeGift}
                                                    className="w-full py-2 text-sm font-medium text-gray-600"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-sm text-gray-600 text-center py-6">
                                            Scan a QR code first using <span className="font-semibold">Send</span> on the wallet page.
                                        </p>
                                    )}
                                </div>
                            )}

                            {giftStep === "confirm" && (
                                <div className="space-y-5">
                                    <div className="text-center">
                                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pay</p>
                                        <p className="text-xl font-bold text-gray-900">{recipientDisplayName}</p>
                                        <p className="text-3xl font-extrabold text-gray-900 mt-3">
                                            {displayINRCurrency(Number(String(giftForm.amount || "").trim()) || 0)}
                                        </p>
                                        {String(giftForm.message || "").trim() && (
                                            <p className="text-sm text-gray-600 mt-3 px-2">
                                                “{String(giftForm.message || "").trim()}”
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-2 pt-1">
                                        <LoadingButton
                                            type="button"
                                            onClick={submitGift}
                                            loading={giftSubmitting}
                                            loadingText="Sending..."
                                            variant="primary"
                                            size="md"
                                            className="w-full py-3.5 rounded-2xl font-bold text-base shadow-lg"
                                        >
                                            Send now
                                        </LoadingButton>
                                        <button
                                            type="button"
                                            onClick={() => setGiftStep("form")}
                                            disabled={giftSubmitting}
                                            className="w-full py-2 text-sm font-medium text-gray-600 disabled:opacity-50"
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            )}

                            {giftStep === "success" && (
                                <div className="space-y-4">
                                    <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-900">
                                        <p className="font-semibold">Gift sent successfully.</p>
                                        {giftResult?.referenceId && (
                                            <p className="mt-2 text-green-800">
                                                <span className="font-medium">Reference ID:</span> {giftResult.referenceId}
                                            </p>
                                        )}
                                        {giftResult?.recipientName && (
                                            <p className="mt-2 text-green-800">
                                                Sent to <span className="font-semibold">{giftResult.recipientName}</span>
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex gap-3 pt-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                resetGift();
                                                setGiftOpen(false);
                                            }}
                                            className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold shadow-md hover:shadow-lg transition-all"
                                        >
                                            Done
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                resetGift();
                                            }}
                                            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
                                        >
                                            Send another
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <QrScannerModal
                    isOpen={qrOpen}
                    onClose={() => setQrOpen(false)}
                    onDetected={onQrDetected}
                />
            </div>
        </section>
    );
};

export default WalletOverviewCompact;
